import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Filter } from "lucide-react";
import { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import { api, apiBaseUrl } from "@/lib/api";
import { formatAdminRouteLine, formatIpReason } from "@/utils/adminDisplay";

const PAGE_SIZE = 10;

export default function IpAccessLogsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (pageNum = page) => {
      setLoading(true);
      try {
        const { data } = await api.get("/api/admin/security/access-logs", {
          params: {
            status: status || undefined,
            search: search || undefined,
            limit: PAGE_SIZE,
            page: pageNum,
          },
        });
        setItems(data.items || []);
        setTotal(data.total || 0);
        setPage(data.page || pageNum);
        setPages(data.pages || 1);
      } finally {
        setLoading(false);
      }
    },
    [status, search, page],
  );

  useEffect(() => {
    load(1);
  }, []);

  function applyFilters() {
    setPage(1);
    load(1);
  }

  function goToPage(next) {
    if (next < 1 || next > pages || next === page) return;
    setPage(next);
    load(next);
  }

  const exportUrl = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    return `${apiBaseUrl()}/api/admin/security/access-logs/export.csv?${params}`;
  };

  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div>
      <Panel className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-[12px]">
            <span className="text-muted-foreground block mb-1">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-[36px] px-3 bg-background border border-line rounded-md"
            >
              <option value="">All</option>
              <option value="allowed">Allowed</option>
              <option value="denied">Denied</option>
              <option value="blocked">Blocked</option>
            </select>
          </label>
          <label className="text-[12px] flex-1 min-w-[180px]">
            <span className="text-muted-foreground block mb-1">Search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="IP, email, route"
              className="w-full h-[36px] px-3 bg-background border border-line rounded-md"
            />
          </label>
          <Btn onClick={applyFilters} data-testid="access-logs-filter">
            <Filter size={14} /> Apply
          </Btn>
          <a href={exportUrl()} target="_blank" rel="noreferrer">
            <Btn data-testid="access-logs-export">
              <Download size={14} /> CSV
            </Btn>
          </a>
        </div>
      </Panel>

      <Panel>
        <div className="p-4 border-b border-line flex flex-wrap items-center justify-between gap-3 text-[13px] text-muted-foreground">
          <span>
            {loading
              ? "Loading…"
              : total === 0
                ? "No events"
                : `Showing ${from}–${to} of ${total} events`}
          </span>
          <span className="text-[11px] text-dim">
            Denied/blocked kept 90 days · auto-deleted (configurable on server)
          </span>
        </div>

        <table className="w-full text-[12px]">
          <thead className="bg-background text-muted-foreground text-left text-[11px] uppercase">
            <tr>
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">IP</th>
              <th className="px-4 py-2">Admin</th>
              <th className="px-4 py-2">Page / action</th>
              <th className="px-4 py-2">Result</th>
              <th className="px-4 py-2">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  No access logs yet.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id || row.log_id} className="hover:bg-surface-2">
                  <td className="px-4 py-2 text-muted-foreground">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2 font-mono">{row.ip_address}</td>
                  <td className="px-4 py-2">{row.email || "—"}</td>
                  <td className="px-4 py-2 text-foreground">
                    {formatAdminRouteLine(row.route, row.method)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill value={row.status} />
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{formatIpReason(row.reason)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {total > 0 && (
          <div className="p-4 border-t border-line flex items-center justify-between gap-3">
            <Btn
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || loading}
              data-testid="access-logs-prev"
            >
              <ChevronLeft size={14} /> Previous
            </Btn>
            <span className="text-[12px] text-muted-foreground">
              Page {page} of {pages}
            </span>
            <Btn
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= pages || loading}
              data-testid="access-logs-next"
            >
              Next <ChevronRight size={14} />
            </Btn>
          </div>
        )}
      </Panel>
    </div>
  );
}
